import { Monaco, OnMount } from '@monaco-editor/react';
import { IDisposable } from 'monaco-editor';
import * as React from 'react';
import { forwardRef } from 'react';
import { CodeLanguages } from 'js/Constants';
import {
  nexlaFnDefinitions,
  nexlaFnModuleTS,
  nexlaFnModuleUriStr,
} from 'js/constants/nexla_fn.definition';
import LoadableMonacoEditor from '../../../editors/monaco/LoadableMonacoEditor';
import { MonacoCodeEditorProps, MonacoRef } from '../../../editors/monaco/MonacoCodeEditor';

const getLanguageSpecificDefinitions = (lang: string) => {
  switch (lang) {
    case CodeLanguages.PYTHON3:
      return nexlaFnDefinitions.filter((def: any) =>
        def?.supportedLanguages?.includes(CodeLanguages.PYTHON3),
      );

    case CodeLanguages.JAVASCRIPT:
      return nexlaFnDefinitions.filter((def: any) =>
        def?.supportedLanguages?.includes(CodeLanguages.JAVASCRIPT),
      );

    case CodeLanguages.JOLT:
      return nexlaFnDefinitions.filter((def: any) =>
        def?.supportedLanguages?.includes(CodeLanguages.JOLT),
      );

    case CodeLanguages.PYTHON:
      return nexlaFnDefinitions.filter((def: any) =>
        def?.supportedLanguages?.includes(CodeLanguages.PYTHON),
      );

    default:
      // Return only those definitions that DO NOT have supportedLanguages defined
      return nexlaFnDefinitions.filter((def: any) => !def?.supportedLanguages);
  }
};

const setupMonaco = (monaco: Monaco, language: string): IDisposable[] => {
  const { editor, Uri, languages } = monaco;
  const {
    CompletionItemInsertTextRule,
    CompletionItemKind,
    registerCompletionItemProvider,
    typescript: { javascriptDefaults },
  } = languages;

  const kind = CompletionItemKind.Function;
  const insertTextRules = CompletionItemInsertTextRule.InsertAsSnippet;

  const languageSpecificDefinitions = getLanguageSpecificDefinitions(language);

  const provideCompletionItems = (model, position) => {
    const word = model.getWordUntilPosition(position);
    const range = {
      startLineNumber: position.lineNumber,
      endLineNumber: position.lineNumber,
      startColumn: word.startColumn,
      endColumn: word.endColumn,
    };

    return {
      suggestions: languageSpecificDefinitions.map((item) => ({
        kind,
        range,
        insertTextRules,
        insertText: item.snippet,
        documentation: item.documentation,
        detail: item.detail,
        label: item.label,
      })),
    };
  };

  const nexlaFnModuleUriObj = Uri.parse(nexlaFnModuleUriStr);
  const uriStr = nexlaFnModuleUriObj.toString();
  const nexlaFnModule =
    editor.getModels().find((model) => model.uri.toString() === uriStr) ||
    editor.createModel(nexlaFnModuleTS, 'typescript', nexlaFnModuleUriObj);

  return [
    registerCompletionItemProvider('javascript', { provideCompletionItems }),
    registerCompletionItemProvider('python', { provideCompletionItems }),
    javascriptDefaults.addExtraLib(nexlaFnModuleTS, nexlaFnModuleUriStr),
    nexlaFnModule,
  ];
};

export const TransformCodeMonacoEditor = forwardRef<MonacoRef, MonacoCodeEditorProps>(
  (props, ref) => {
    const language = props.language;
    const onEditorReady: OnMount = (editor, monaco: Monaco) => {
      return [...(setupMonaco(monaco, language) || []), ...(props.onMount?.(editor, monaco) || [])];
    };

    return (
      <LoadableMonacoEditor
        /**
         * key is used to force a re-render of the editor when the language changes,
         * so that the language specific definitions are loaded
         */
        key={language}
        theme="dark"
        {...props}
        ref={ref}
        onMount={onEditorReady}
      />
    );
  },
);
